import { useState, useEffect, useCallback } from 'react';
import { getSignedUrl } from '@/api/pets';

const urlCache: Record<string, string> = {};

/**
 * Resolves a storage key to a signed display URL for social post images.
 * If the value is already a URL (http/https/blob), returns it as-is.
 */
export function useSignedDocumentUrl(
  keyOrUrl: string | undefined | null
): { url: string | null; loading: boolean } {
  const [url, setUrl] = useState<string | null>(() => {
    if (!keyOrUrl) return null;
    if (
      keyOrUrl.startsWith('http://') ||
      keyOrUrl.startsWith('https://') ||
      keyOrUrl.startsWith('blob:') ||
      keyOrUrl.startsWith('data:')
    ) {
      return keyOrUrl;
    }
    return urlCache[keyOrUrl] ?? null;
  });
  const [loading, setLoading] = useState(!url && !!keyOrUrl);

  const resolve = useCallback(async (key: string) => {
    if (urlCache[key]) {
      setUrl(urlCache[key]);
      setLoading(false);
      return;
    }
    try {
      const { url: signedUrl } = await getSignedUrl(key, 'inline');
      urlCache[key] = signedUrl;
      setUrl(signedUrl);
    } catch (err) {
      console.error('Failed to get signed URL for post image:', err);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!keyOrUrl) {
      setUrl(null);
      setLoading(false);
      return;
    }
    if (
      keyOrUrl.startsWith('http://') ||
      keyOrUrl.startsWith('https://') ||
      keyOrUrl.startsWith('blob:') ||
      keyOrUrl.startsWith('data:')
    ) {
      setUrl(keyOrUrl);
      setLoading(false);
      return;
    }
    setLoading(true);
    resolve(keyOrUrl);
  }, [keyOrUrl, resolve]);

  return { url, loading };
}
